import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../../auth/AuthProvider';
import { isFirstMachineActivationSession } from '../../../auth/first-machine-activation-session';
import { useModalFocusTrap } from '../accessibility/use-modal-focus-trap';
import { recordProductTourEventBestEffort, rememberProductTourEntryPoint } from '../analytics/product-tour-analytics';
import { rememberProductTourReturnPath } from '../navigation/product-tour-return';
import {
  getProductTourState,
  transitionProductTourState,
} from '../../product-guidance/api/product-tour-state.api';

interface ProductTourOnboardingOfferProps {
  machineCount: number | null;
  machinesReady: boolean;
}

export function ProductTourOnboardingOffer({ machineCount, machinesReady }: ProductTourOnboardingOfferProps) {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLElement>(null);
  const offeredCustomerRef = useRef<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dismissedCustomerId, setDismissedCustomerId] = useState<string | null>(null);

  const customerId = auth.activeCustomerId;
  const organizationSelection = location.pathname === '/organizations' || location.pathname === '/customers';
  const eligibleSurface = Boolean(
    customerId &&
    machinesReady &&
    (machineCount ?? 0) > 0 &&
    !organizationSelection &&
    !isFirstMachineActivationSession(customerId),
  );

  useEffect(() => {
    let active = true;
    setVisible(false);
    setBusy(false);

    if (!customerId || !eligibleSurface || dismissedCustomerId === customerId) return () => { active = false; };

    void getProductTourState()
      .then((state) => {
        if (!active) return;
        const shouldOffer = state.status === 'NOT_STARTED';
        setVisible(shouldOffer);
        if (shouldOffer && offeredCustomerRef.current !== customerId) {
          offeredCustomerRef.current = customerId;
          recordProductTourEventBestEffort({ event_type: 'TOUR_OFFERED', entry_point: 'ONBOARDING' });
        }
      })
      .catch(() => {
        // Product education should never block the real workspace. If the
        // guidance state service is unavailable, fail open and let the person
        // continue working normally.
        if (active) setVisible(false);
      });

    return () => {
      active = false;
    };
  }, [customerId, dismissedCustomerId, eligibleSurface]);

  const continueToWorkspace = useCallback(async () => {
    if (!customerId) return;
    setBusy(true);
    setVisible(false);
    setDismissedCustomerId(customerId);
    recordProductTourEventBestEffort({ event_type: 'TOUR_SKIPPED' });
    try {
      await transitionProductTourState('SKIP');
    } catch {
      // The user's explicit choice still wins for this session. A failed
      // preference write must not trap them behind onboarding.
    } finally {
      setBusy(false);
    }
  }, [customerId]);

  const startTour = useCallback(() => {
    if (!customerId) return;
    const returnPath = `${location.pathname}${location.search}${location.hash}`;
    rememberProductTourReturnPath(returnPath);
    rememberProductTourEntryPoint('ONBOARDING');
    setDismissedCustomerId(customerId);
    setVisible(false);
    navigate('/tour?guided=1');
  }, [customerId, location.hash, location.pathname, location.search, navigate]);

  const handleEscape = useCallback(() => {
    void continueToWorkspace();
  }, [continueToWorkspace]);

  useModalFocusTrap(dialogRef, visible, handleEscape);

  if (!visible || !customerId) return null;

  return (
    <div className="r42-dialog-layer r42-tour-onboarding-layer" role="dialog" aria-modal="true" aria-labelledby="tour-onboarding-title" aria-describedby="tour-onboarding-description">
      <div className="r42-dialog-backdrop" aria-hidden="true" />
      <section ref={dialogRef} className="r42-dialog-card r42-tour-onboarding-card" tabIndex={-1}>
        <div className="r42-tour-onboarding-icon" aria-hidden="true"><i className="bi bi-stars" /></div>
        <span className="r42-eyebrow">Welcome to Route42</span>
        <h2 id="tour-onboarding-title">See how the pieces work together.</h2>
        <p id="tour-onboarding-description">
          Take a short guided example to see how evidence, questions, Investigations, and Machine Memory build useful machine understanding together.
        </p>
        <div className="r42-tour-onboarding-assurance" role="note">
          <i className="bi bi-shield-check" aria-hidden="true" />
          <span>Uses fictional machine data. Nothing in the tour changes your organization's information.</span>
        </div>
        <div className="r42-tour-onboarding-meta">
          <span><i className="bi bi-clock" aria-hidden="true" /> About 3 minutes</span>
          <span><i className="bi bi-skip-forward" aria-hidden="true" /> Skip anytime</span>
        </div>
        <div className="r42-dialog-actions r42-tour-onboarding-actions">
          <button className="btn btn-outline-secondary" type="button" onClick={() => void continueToWorkspace()} disabled={busy}>
            {busy ? 'Continuing…' : 'Go to my workspace'}
          </button>
          <button data-autofocus="true" className="btn r42-btn-primary" type="button" onClick={startTour} disabled={busy}>
            See Route42 in action <i className="bi bi-arrow-right" aria-hidden="true" />
          </button>
        </div>
      </section>
    </div>
  );
}
